import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import type { CardData, Suit } from '../game/types';
import { Card } from './Board';
import { LAYOUT_BLOCK_SIZE, suggestLayout } from '../game/deckLayout';
import { syndicateFor, type Syndicate } from '../game/syndicates';

// Badge del Syndicate: nome della banda + bonus passivo della coppia di suit.
function SyndicateBadge({ syn }: { syn: Syndicate }) {
  return (
    <div style={{
      display: 'inline-flex', flexDirection: 'column', gap: 2,
      border: '1px solid #ffb703', background: '#1a1608', borderRadius: 6,
      padding: '6px 10px', color: '#ffb703', fontSize: 12, textAlign: 'left',
    }}>
      <span style={{ fontWeight: 'bold' }}>★ Syndicate: {syn.name}</span>
      <span style={{ color: '#ccc', fontSize: 11 }}>{syn.bonus}</span>
    </div>
  );
}

import { SERVER_ORIGIN } from '../config';
const SUIT_ORDER: Suit[] = ['drug', 'money', 'cars', 'police', 'jewels', 'casino chips', 'criminal contacts', 'weapons'];
const ACTIVE_SUITS_FOR_BUILD: Suit[] = ['drug', 'money', 'cars', 'police', 'jewels', 'casino chips', 'criminal contacts', 'weapons'];

interface UserCardRecord {
  id: string;
  card: CardData;
  acquiredAt: string;
  source: string;
}

interface UserDeckRecord {
  id: string;
  name: string;
  createdAt: string;
  cards: UserCardRecord[];
}

interface DeckEditorProps {
  mode: 'create' | 'edit';
  existingDeck?: UserDeckRecord;
  cards: UserCardRecord[];
  token: string;
  onClose: () => void;
  onSaved: (deck: UserDeckRecord) => void;
}

// Check completeness of a suit in the user's inventory.
function isSuitComplete(cards: UserCardRecord[], suit: Suit): boolean {
  const handValues = new Set<number>();
  let hasMission = false;
  for (const r of cards) {
    if (r.card.suit !== suit) continue;
    if (r.card.type === 'Hand' && r.card.value >= 1 && r.card.value <= 6) {
      handValues.add(r.card.value);
    } else if (r.card.type === 'Mission') {
      hasMission = true;
    }
  }
  return handValues.size === 6 && hasMission;
}

// Validation result for a tentative deck composition.
function validateDeck(
  selectedIds: Set<string>,
  cards: UserCardRecord[],
  suits: [Suit, Suit] | null
): { valid: boolean; missing: string[]; count: number } {
  if (!suits) return { valid: false, missing: [], count: selectedIds.size };
  const byCardId = new Map(cards.map(r => [r.id, r] as const));
  const bySuit = new Map<Suit, { handValues: Set<number>; missions: number }>([
    [suits[0], { handValues: new Set(), missions: 0 }],
    [suits[1], { handValues: new Set(), missions: 0 }],
  ]);
  for (const id of selectedIds) {
    const r = byCardId.get(id);
    if (!r) continue;
    const slot = bySuit.get(r.card.suit as Suit);
    if (!slot) continue;
    if (r.card.type === 'Hand') slot.handValues.add(r.card.value);
    else if (r.card.type === 'Mission') slot.missions += 1;
  }
  const missing: string[] = [];
  for (const suit of suits) {
    const slot = bySuit.get(suit)!;
    for (let v = 1; v <= 6; v++) {
      if (!slot.handValues.has(v)) missing.push(`${suit} ${v}`);
    }
    if (slot.missions === 0) missing.push(`${suit} Mission`);
  }
  return { valid: missing.length === 0 && selectedIds.size === 14, missing, count: selectedIds.size };
}

// Disposizione a blocchi del mazzo: le 12 Hand card in tre blocchi da 4
// (tavolo / mano 1 / mano 2, vedi game/deckLayout.ts) + le 2 Mission.
// Salvata come ordine delle carte (user_deck_cards.position).
type BlockKey = 'table' | 'hand1' | 'hand2';
const BLOCK_KEYS: BlockKey[] = ['table', 'hand1', 'hand2'];
interface Blocks { table: string[]; hand1: string[]; hand2: string[]; missions: string[] }
const EMPTY_BLOCKS: Blocks = { table: [], hand1: [], hand2: [], missions: [] };
const BLOCK_INFO: Record<BlockKey | 'missions', { label: string; subtitle: string; max: number }> = {
  table: { label: 'Table', subtitle: 'Start on the dropzones: 2 per dropzone, which 2 is random.', max: LAYOUT_BLOCK_SIZE },
  hand1: { label: 'Hand 1', subtitle: 'Your first hand of the match.', max: LAYOUT_BLOCK_SIZE },
  hand2: { label: 'Hand 2', subtitle: 'Your second hand of the match.', max: LAYOUT_BLOCK_SIZE },
  missions: { label: 'Missions', subtitle: 'One of the two is drawn at random each match.', max: 2 },
};

function blocksFromOrderedIds(ids: string[], cards: UserCardRecord[]): Blocks {
  const byId = new Map(cards.map(r => [r.id, r] as const));
  const hand = ids.filter(id => byId.get(id)?.card.type === 'Hand');
  return {
    table: hand.slice(0, 4),
    hand1: hand.slice(4, 8),
    hand2: hand.slice(8, 12),
    missions: ids.filter(id => byId.get(id)?.card.type === 'Mission'),
  };
}

function blockOf(b: Blocks, id: string): keyof Blocks | null {
  for (const k of [...BLOCK_KEYS, 'missions'] as const) if (b[k].includes(id)) return k;
  return null;
}

function removeFromBlocks(b: Blocks, cardId: string): Blocks {
  return {
    table: b.table.filter(id => id !== cardId),
    hand1: b.hand1.filter(id => id !== cardId),
    hand2: b.hand2.filter(id => id !== cardId),
    missions: b.missions.filter(id => id !== cardId),
  };
}

// Il puntatore decide il bersaglio; se è sopra una carta del mazzo, quella
// vince sul blocco che la contiene (drop = scambio).
const collisionDetection: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  const slot = hits.find(h => String(h.id).startsWith('slot:'));
  if (slot) return [slot];
  return hits.length > 0 ? hits : rectIntersection(args);
};

// Returns true if card B should replace card A when added (same suit + same
// "slot": same Hand value, or both Mission).
function isSameSlot(a: CardData, b: CardData): boolean {
  if (a.suit !== b.suit) return false;
  if (a.type === 'Hand' && b.type === 'Hand') return a.value === b.value;
  if (a.type === 'Mission' && b.type === 'Mission') return true;
  return false;
}

interface DraggableCardProps {
  record: UserCardRecord;
  inDeck: boolean;
  onClick: () => void;
  highlightDuplicate: boolean;
}

function DraggableCard({ record, inDeck, onClick, highlightDuplicate }: DraggableCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: record.id });
  // Le carte nel mazzo sono anche bersagli di drop: rilasciarci sopra un'altra
  // carta del mazzo le scambia di blocco (tavolo / mano 1 / mano 2).
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `slot:${record.id}`, disabled: !inDeck });
  return (
    <div
      ref={(el) => { setNodeRef(el); setDropRef(el); }}
      {...attributes}
      {...listeners}
      onClick={onClick}
      style={{
        cursor: 'pointer',
        opacity: isDragging ? 0.3 : 1,
        position: 'relative',
        outline: highlightDuplicate ? '2px solid #ffb703' : isOver ? '2px solid var(--accent-color)' : 'none',
        outlineOffset: '2px',
        borderRadius: '8px',
      }}
      title={inDeck
        ? 'Click to remove · drag onto another block or card to rearrange'
        : highlightDuplicate
          ? 'Duplicate — click or drag to pick this instance'
          : 'Click or drag to add'}
    >
      <Card card={record.card} staticContext />
    </div>
  );
}

interface DropZoneProps {
  id: string;
  children: React.ReactNode;
  label: string;
  subtitle?: string;
}

function DropZone({ id, children, label, subtitle, compact }: DropZoneProps & { compact?: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      style={compact ? {
        // Nella colonna flex con altezza massima i blocchi NON devono
        // restringersi: crescono con le carte e la colonna scrolla.
        flexShrink: 0,
        background: isOver ? '#22222c' : '#16161e',
        border: `2px ${isOver ? 'solid var(--accent-color)' : 'dashed #444'}`,
        borderRadius: '8px',
        padding: '10px',
        minHeight: '120px',
        transition: 'background 0.15s, border-color 0.15s',
      } : {
        flex: 1,
        background: isOver ? '#22222c' : '#16161e',
        border: `2px ${isOver ? 'solid var(--accent-color)' : 'dashed #444'}`,
        borderRadius: '8px',
        padding: '14px',
        minHeight: '420px',
        transition: 'background 0.15s, border-color 0.15s',
        overflowY: 'auto',
        maxHeight: '60vh',
      }}
    >
      <div style={{ color: 'var(--accent-color)', fontWeight: 'bold', marginBottom: '4px' }}>{label}</div>
      {subtitle && <div style={{ color: '#888', fontSize: '12px', marginBottom: '12px' }}>{subtitle}</div>}
      {children}
    </div>
  );
}

export function DeckEditor({ mode, existingDeck, cards, token, onClose, onSaved }: DeckEditorProps) {
  // Derive initial suits + selectedIds from existing deck if in edit mode.
  const initialSuits = useMemo<[Suit, Suit] | null>(() => {
    if (mode !== 'edit' || !existingDeck) return null;
    const ss = Array.from(new Set(existingDeck.cards.map(c => c.card.suit))) as Suit[];
    if (ss.length === 2) return [ss[0], ss[1]];
    return null;
  }, [mode, existingDeck]);
  const initialBlocks = useMemo<Blocks>(() => {
    if (mode !== 'edit' || !existingDeck) return EMPTY_BLOCKS;
    // existingDeck.cards è già in ordine di position.
    return blocksFromOrderedIds(existingDeck.cards.map(c => c.id), existingDeck.cards);
  }, [mode, existingDeck]);

  const [step, setStep] = useState<'suits' | 'cards'>(initialSuits ? 'cards' : 'suits');
  const [name, setName] = useState(existingDeck?.name ?? '');
  // Suit picker uses an array (0, 1, or 2 items). `suits` getter wraps to tuple.
  const [suitPicks, setSuitPicks] = useState<Suit[]>(initialSuits ?? []);
  const suits: [Suit, Suit] | null = suitPicks.length === 2 ? [suitPicks[0], suitPicks[1]] : null;
  const [blocks, setBlocks] = useState<Blocks>(initialBlocks);
  const selectedIds = useMemo(
    () => new Set([...blocks.table, ...blocks.hand1, ...blocks.hand2, ...blocks.missions]),
    [blocks],
  );

  const toggleSuitPick = (s: Suit) => {
    setSuitPicks(prev => {
      const next = prev.includes(s)
        ? prev.filter(x => x !== s)
        : [...prev, s].slice(-2); // rimuove il più vecchio se già 2
      // Cambiando brand, il vecchio set di carte selezionate non è più valido
      // (potrebbe riferirsi a suit non più scelte). Reset.
      setBlocks(EMPTY_BLOCKS);
      return next;
    });
  };
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // For suit step: which suits are "buildable" (have complete inventory).
  const completeSuits = useMemo(() => {
    return ACTIVE_SUITS_FOR_BUILD.filter(s => isSuitComplete(cards, s));
  }, [cards]);

  // Cards filtered to the 2 chosen suits.
  const filteredCards = useMemo(() => {
    if (!suits) return [];
    return cards.filter(c => c.card.suit === suits[0] || c.card.suit === suits[1]);
  }, [cards, suits]);

  // Compute "duplicate" cards: same (suit, value) where the player has 2+
  // instances AND none of them is already in deck — these are the ones that
  // require a manual pick.
  const duplicateIds = useMemo(() => {
    const out = new Set<string>();
    if (!suits) return out;
    // Group filtered cards by slot signature, then mark all instances of
    // groups with size >= 2 AND no member already in deck.
    const groups = new Map<string, UserCardRecord[]>();
    for (const r of filteredCards) {
      const key = r.card.type === 'Hand' ? `${r.card.suit}|H|${r.card.value}` : `${r.card.suit}|M`;
      const arr = groups.get(key) ?? [];
      arr.push(r);
      groups.set(key, arr);
    }
    for (const arr of groups.values()) {
      if (arr.length < 2) continue;
      const anyInDeck = arr.some(r => selectedIds.has(r.id));
      if (anyInDeck) continue;
      for (const r of arr) out.add(r.id);
    }
    return out;
  }, [filteredCards, selectedIds, suits]);

  const validation = useMemo(() => validateDeck(selectedIds, cards, suits), [selectedIds, cards, suits]);

  // Cards displayed on each side.
  const cardsInCollection = useMemo(
    () => filteredCards.filter(c => !selectedIds.has(c.id)),
    [filteredCards, selectedIds]
  );
  const cardsInDeck = useMemo(
    () => filteredCards.filter(c => selectedIds.has(c.id)),
    [filteredCards, selectedIds]
  );

  // Aggiunge una carta al mazzo (funzione pura su Blocks). Se lo slot
  // (stessa suit + stesso valore, o Mission della stessa suit) è già occupato
  // da un'altra istanza, la sostituisce NELLA STESSA posizione. Altrimenti va
  // nel blocco preferito se ha spazio, poi nel primo blocco libero.
  const addToBlocks = (b: Blocks, cardId: string, preferred?: BlockKey): Blocks => {
    const r = cards.find(c => c.id === cardId);
    if (!r) return b;
    const next: Blocks = { table: [...b.table], hand1: [...b.hand1], hand2: [...b.hand2], missions: [...b.missions] };
    for (const k of [...BLOCK_KEYS, 'missions'] as const) {
      const idx = next[k].findIndex(id => {
        const other = cards.find(c => c.id === id);
        return other ? isSameSlot(other.card, r.card) : false;
      });
      if (idx !== -1) { next[k][idx] = cardId; return next; }
    }
    if (r.card.type === 'Mission') {
      if (next.missions.length < 2) next.missions.push(cardId);
      return next;
    }
    const order = preferred ? [preferred, ...BLOCK_KEYS.filter(k => k !== preferred)] : BLOCK_KEYS;
    const target = order.find(k => next[k].length < LAYOUT_BLOCK_SIZE);
    if (target) next[target].push(cardId);
    return next;
  };

  // Click: dentro/fuori dal mazzo.
  const toggleCard = (cardId: string) => {
    setBlocks(b => (blockOf(b, cardId) ? removeFromBlocks(b, cardId) : addToBlocks(b, cardId)));
  };

  // Sposta una carta del mazzo in un altro blocco (se c'è spazio).
  const moveToBlock = (cardId: string, target: BlockKey) => {
    setBlocks(b => {
      const from = blockOf(b, cardId);
      if (!from || from === 'missions' || from === target || b[target].length >= LAYOUT_BLOCK_SIZE) return b;
      const next = removeFromBlocks(b, cardId);
      next[target] = [...next[target], cardId];
      return next;
    });
  };

  // Scambia di posto due carte del mazzo (anche tra blocchi diversi).
  const swapCards = (a: string, c: string) => {
    setBlocks(b => {
      const ka = blockOf(b, a);
      const kc = blockOf(b, c);
      if (!ka || !kc || ka === 'missions' || kc === 'missions') return b;
      const next: Blocks = { table: [...b.table], hand1: [...b.hand1], hand2: [...b.hand2], missions: [...b.missions] };
      next[ka][next[ka].indexOf(a)] = c;
      next[kc][next[kc].indexOf(c)] = a;
      return next;
    });
  };

  // Disposizione suggerita (stessa regola usata per mazzi starter e bot).
  const applySuggestedLayout = () => {
    const recs = [...blocks.table, ...blocks.hand1, ...blocks.hand2, ...blocks.missions]
      .map(id => cards.find(c => c.id === id))
      .filter((r): r is UserCardRecord => !!r);
    const ordered = suggestLayout(recs.map(r => ({ ...r.card, id: r.id })));
    setBlocks(blocksFromOrderedIds(ordered.map(c => c.id), cards));
  };

  // "Aggiungi automatiche": per ogni (suit, slot) dove esiste UNA SOLA
  // istanza disponibile e nessuna istanza dello stesso slot è già in deck,
  // aggiungila al deck.
  const autoAddSingles = () => {
    if (!suits) return;
    let nextBlocks = blocks;
    const next = new Set(selectedIds);
    const slotKey = (c: CardData) => c.type === 'Hand' ? `${c.suit}|H|${c.value}` : `${c.suit}|M`;
    const groups = new Map<string, UserCardRecord[]>();
    for (const r of filteredCards) {
      const k = slotKey(r.card);
      const arr = groups.get(k) ?? [];
      arr.push(r);
      groups.set(k, arr);
    }
    for (const [, arr] of groups) {
      const alreadyInDeck = arr.some(r => next.has(r.id));
      if (alreadyInDeck) continue;
      if (arr.length === 1) {
        next.add(arr[0].id);
        nextBlocks = addToBlocks(nextBlocks, arr[0].id);
      }
    }
    setBlocks(nextBlocks);
  };

  const clearDeck = () => setBlocks(EMPTY_BLOCKS);

  const handleDragStart = (e: DragStartEvent) => setActiveDragId(String(e.active.id));
  const handleDragEnd = (e: DragEndEvent) => {
    setActiveDragId(null);
    if (!e.over) return;
    const cardId = String(e.active.id);
    const overId = String(e.over.id);
    const inDeck = selectedIds.has(cardId);
    if (overId === 'collection') {
      if (inDeck) toggleCard(cardId);
      return;
    }
    if (overId.startsWith('slot:')) {
      const targetId = overId.slice(5);
      if (targetId === cardId) return;
      if (inDeck) swapCards(cardId, targetId);
      else setBlocks(b => addToBlocks(b, cardId, (blockOf(b, targetId) ?? undefined) as BlockKey | undefined));
      return;
    }
    if (overId.startsWith('block:')) {
      const target = overId.slice(6) as BlockKey | 'missions';
      if (!inDeck) setBlocks(b => addToBlocks(b, cardId, target === 'missions' ? undefined : target));
      else if (target !== 'missions') moveToBlock(cardId, target);
    }
  };

  const handleSave = async () => {
    if (!validation.valid || !name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      // Ordine = disposizione: [tavolo, mano 1, mano 2, missioni].
      const cardIds = [...blocks.table, ...blocks.hand1, ...blocks.hand2, ...blocks.missions];
      const url = mode === 'create'
        ? `${SERVER_ORIGIN}/auth/decks`
        : `${SERVER_ORIGIN}/auth/decks/${existingDeck!.id}`;
      const res = await fetch(url, {
        method: mode === 'create' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), cardIds }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? 'save failed'); return; }
      onSaved(data.deck);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'network error');
    } finally {
      setSaving(false);
    }
  };

  // -------------------- Render --------------------

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  };

  const panelStyle: React.CSSProperties = {
    background: '#0e0e16',
    border: '2px solid var(--accent-color)',
    borderRadius: '10px',
    padding: '24px',
    maxWidth: '1200px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 0 40px rgba(0, 200, 255, 0.3)',
  };

  const activeRecord = activeDragId ? cards.find(c => c.id === activeDragId) : null;

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ color: 'var(--accent-color)', margin: 0 }}>
            {mode === 'create' ? 'Create new deck' : `Edit deck`}
          </h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', fontSize: '24px', cursor: 'pointer' }} title="Close">×</button>
        </div>

        {error && (
          <div style={{ color: '#e57373', background: '#2a1010', padding: '8px 12px', borderRadius: '4px', marginBottom: '12px' }}>
            {error}
          </div>
        )}

        {step === 'suits' && (
          <div>
            <p style={{ color: '#aaa', marginTop: 0 }}>
              Pick two "brands" (suits) for your deck. Only brands of which you own all 6 Hand cards (1-6) + at least one Mission can be selected.
            </p>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center', margin: '24px 0' }}>
              {SUIT_ORDER.map(s => {
                if (!ACTIVE_SUITS_FOR_BUILD.includes(s)) return null;
                const selectable = completeSuits.includes(s);
                const selected = suitPicks.includes(s);
                return (
                  <button
                    key={s}
                    disabled={!selectable}
                    onClick={() => toggleSuitPick(s)}
                    style={{
                      width: '120px',
                      height: '140px',
                      background: selected ? '#1a2a3a' : '#1a1a24',
                      border: `3px solid ${selected ? 'var(--accent-color)' : selectable ? '#444' : '#222'}`,
                      borderRadius: '12px',
                      cursor: selectable ? 'pointer' : 'not-allowed',
                      opacity: selectable ? 1 : 0.35,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      transition: 'all 0.15s',
                      boxShadow: selected ? '0 0 16px var(--accent-color)' : 'none',
                    }}
                    title={selectable ? s : `Incomplete inventory for ${s}`}
                  >
                    <span
                      className={`suit-icon suit-icon-${s.replace(/\s+/g, '-')}`}
                      style={{ width: '64px', height: '64px' }}
                    />
                    <span style={{ color: selected ? 'var(--accent-color)' : '#bbb', fontSize: '13px', textTransform: 'capitalize' }}>
                      {s}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ color: '#888', fontSize: '13px', textAlign: 'center', marginBottom: '20px' }}>
              {suitPicks.length === 0 && 'Select 2 brands to continue'}
              {suitPicks.length === 1 && `Selected: ${suitPicks[0]} — pick another`}
              {suitPicks.length === 2 && `Selected: ${suitPicks[0]} + ${suitPicks[1]}`}
              {suitPicks.length === 2 && syndicateFor(suitPicks) && (
                <div style={{ marginTop: 10 }}><SyndicateBadge syn={syndicateFor(suitPicks)!} /></div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={onClose}
                style={{ background: '#333', border: 'none', color: '#bbb', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer' }}
              >Cancel</button>
              <button
                disabled={!suits}
                onClick={() => setStep('cards')}
                style={{
                  background: suits ? 'var(--accent-color)' : '#333',
                  border: 'none',
                  color: suits ? '#000' : '#666',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: suits ? 'pointer' : 'not-allowed',
                }}
              >Next →</button>
            </div>
          </div>
        )}

        {step === 'cards' && suits && (
          <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <label style={{ color: '#bbb', fontSize: '13px' }}>Name:</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Drug & Money"
                maxLength={64}
                style={{
                  flex: 1,
                  background: '#1a1a24',
                  border: '2px solid #444',
                  color: '#ddd',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                }}
              />
              <span style={{ color: '#888', fontSize: '12px' }}>
                {suits[0]} + {suits[1]}
              </span>
              {syndicateFor(suits) && <SyndicateBadge syn={syndicateFor(suits)!} />}
              {mode === 'create' && (
                <button
                  onClick={() => setStep('suits')}
                  style={{ background: 'transparent', border: '1px solid #444', color: '#aaa', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                >
                  ← Change brands
                </button>
              )}
            </div>

            <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
              <button
                onClick={autoAddSingles}
                style={{
                  background: '#1a2a3a',
                  border: '2px solid var(--accent-color)',
                  color: 'var(--accent-color)',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '13px',
                }}
                title="Add all cards you own only one instance of"
              >
                ⚡ Auto-add unique
              </button>
              <button
                onClick={clearDeck}
                disabled={selectedIds.size === 0}
                style={{
                  background: 'transparent',
                  border: '2px solid #555',
                  color: selectedIds.size === 0 ? '#555' : '#aaa',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                }}
              >
                Clear deck
              </button>
              <div style={{ flex: 1 }} />
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 14px',
                borderRadius: '6px',
                background: validation.valid ? '#1a3a1a' : '#3a2a1a',
                color: validation.valid ? '#4ade80' : '#fbbf24',
                fontWeight: 'bold',
                fontSize: '13px',
              }}>
                {validation.valid
                  ? '✓ Valid deck (14/14)'
                  : `${validation.count}/14 — missing: ${validation.missing.slice(0, 4).join(', ')}${validation.missing.length > 4 ? '…' : ''}`}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '20px' }}>
              <DropZone
                id="collection"
                label="Your cards"
                subtitle="Drag or click to add to the deck. Cards highlighted in yellow are duplicates: pick which instance to use."
              >
                <CardsGroupedBySuit
                  cards={cardsInCollection}
                  suits={suits}
                  duplicateIds={duplicateIds}
                  selectedIds={selectedIds}
                  onCardClick={toggleCard}
                />
              </DropZone>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '60vh', overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--accent-color)', fontWeight: 'bold' }}>Deck ({selectedIds.size}/14)</span>
                  <button
                    onClick={applySuggestedLayout}
                    disabled={!validation.valid}
                    title="Rearrange the blocks with the suggested layout: cards without effects on the table, balanced hands"
                    style={{
                      background: 'transparent',
                      border: `1px solid ${validation.valid ? 'var(--accent-color)' : '#444'}`,
                      color: validation.valid ? 'var(--accent-color)' : '#555',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: validation.valid ? 'pointer' : 'not-allowed',
                    }}
                  >Suggest layout</button>
                </div>
                {([...BLOCK_KEYS, 'missions'] as const).map(k => (
                  <DropZone
                    key={k}
                    id={`block:${k}`}
                    compact
                    label={`${BLOCK_INFO[k].label} (${blocks[k].length}/${BLOCK_INFO[k].max})`}
                    subtitle={BLOCK_INFO[k].subtitle}
                  >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {blocks[k].map(id => {
                        const r = cardsInDeck.find(c => c.id === id);
                        return r ? (
                          <DraggableCard key={id} record={r} inDeck onClick={() => toggleCard(id)} highlightDuplicate={false} />
                        ) : null;
                      })}
                    </div>
                  </DropZone>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={onClose}
                disabled={saving}
                style={{ background: '#333', border: 'none', color: '#bbb', padding: '10px 20px', borderRadius: '6px', cursor: saving ? 'wait' : 'pointer' }}
              >Cancel</button>
              <button
                disabled={!validation.valid || !name.trim() || saving}
                onClick={handleSave}
                style={{
                  background: validation.valid && name.trim() ? 'var(--accent-color)' : '#333',
                  border: 'none',
                  color: validation.valid && name.trim() ? '#000' : '#666',
                  padding: '10px 24px',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: validation.valid && name.trim() && !saving ? 'pointer' : 'not-allowed',
                }}
              >
                {saving ? 'Saving...' : 'Save deck'}
              </button>
            </div>

            <DragOverlay>
              {activeRecord ? (
                <div style={{ transform: 'scale(1.1)', filter: 'drop-shadow(0 0 12px var(--accent-color))' }}>
                  <Card card={activeRecord.card} staticContext />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
    </div>
  );
}

interface CardsGroupedBySuitProps {
  cards: UserCardRecord[];
  suits: [Suit, Suit];
  duplicateIds: Set<string>;
  selectedIds: Set<string>;
  onCardClick: (id: string) => void;
}

function CardsGroupedBySuit({ cards, suits, duplicateIds, selectedIds, onCardClick }: CardsGroupedBySuitProps) {
  const grouped = new Map<Suit, UserCardRecord[]>();
  for (const s of suits) grouped.set(s, []);
  for (const r of cards) {
    const arr = grouped.get(r.card.suit as Suit);
    if (arr) arr.push(r);
  }
  for (const arr of grouped.values()) {
    arr.sort((a, b) => {
      if (a.card.type !== b.card.type) return a.card.type === 'Hand' ? -1 : 1;
      return a.card.value - b.card.value;
    });
  }
  return (
    <>
      {suits.map(suit => {
        const arr = grouped.get(suit) ?? [];
        return (
          <div key={suit} style={{ marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span
                className={`suit-icon suit-icon-${suit.replace(/\s+/g, '-')}`}
                style={{ width: '20px', height: '20px' }}
              />
              <span style={{ color: '#bbb', fontSize: '12px', textTransform: 'capitalize' }}>{suit}</span>
              <span style={{ color: '#666', fontSize: '11px' }}>({arr.length})</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {arr.map(r => (
                <DraggableCard
                  key={r.id}
                  record={r}
                  inDeck={selectedIds.has(r.id)}
                  onClick={() => onCardClick(r.id)}
                  highlightDuplicate={duplicateIds.has(r.id)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}
